import { Tile } from "@ui/components/Tile";
import type { ReactNode } from "react";

export interface EditorNavSection {
  description?: string;
  footer?: ReactNode;
  href: string;
  /** Rendered above the label (e.g. a sticker icon for icon-led sections). */
  icon?: ReactNode;
  key: string;
  label: string;
}

export interface EditorSectionNavProps {
  ariaLabel: string;
  /** Section key of the page being edited; its tile renders as current. */
  currentKey?: string;
  /** `card` (default) previews each section; `row` is the compact icon+label
   *  list for sections that have nothing to preview. */
  layout?: "card" | "row";
  sections: EditorNavSection[];
}

/**
 * The editor's section navigation: one tile per editable section, shared by
 * the editor index pages and the `EditFrame` sidebar. Section content comes
 * pre-rendered on each section (see `cards.tsx`), so this stays entity-agnostic.
 */
export function EditorSectionNav({ ariaLabel, currentKey, layout = "card", sections }: EditorSectionNavProps) {
  const isRow = layout === "row";

  return (
    <nav aria-label={ariaLabel} className={isRow ? "-mx-3 flex flex-col gap-0.5" : "flex flex-col gap-3"}>
      {sections.map((section) => (
        <Tile
          key={section.key}
          description={section.description}
          footer={section.footer}
          hierarchy={isRow ? "plain" : "raised"}
          href={section.href}
          isCurrent={section.key === currentKey}
          label={section.label}
          shape={isRow ? "row" : "rectangular"}
        >
          {section.icon}
        </Tile>
      ))}
    </nav>
  );
}
